/**
 * PromSetupModal — an editorial folio laid over the desk.
 *
 * Mode 2 (Functional Editorial). Per Craig's lock:
 *   • This is NOT an onboarding popup. It is a composed folio the
 *     teacher pulls toward them on the curator's desk to commission
 *     the prom night.
 *   • No stepper bubbles, no checkmark steps, no purple/cyan gradient.
 *   • No "Instantly ✦" or "Done for you" startup copy.
 *   • Restrained gold accent, Georgia serif, editorial pacing.
 *
 * The teacher should feel: "I am preparing the room",
 * NOT: "I am completing onboarding".
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { trackGrowth, getAnonId } from '../lib/growthTrack';
import { MODE_TWO, QuietPress } from './ModeTwoPrimitives';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// The single-line phrases that replace the stepper. The phrase rotates
// quietly while the lead is being composed — one calm sentence at a
// time. NEVER a bubble, NEVER a checkmark.
const COMPOSING_LINES = [
  'Composing the prom page…',
  'Setting the QR plate…',
  'Preparing the link for the room…',
];

export default function PromSetupModal({ open, onClose, onSelfServe }) {
  const navigate = useNavigate();
  const [school, setSchool] = useState('');
  const [date, setDate] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [students, setStudents] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lineIndex, setLineIndex] = useState(0);
  const [error, setError] = useState('');

  // Lock background scroll while the folio is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!school.trim() || !email.trim() || !email.includes('@')) {
      setError('A school name and a valid email are required.');
      return;
    }
    setSubmitting(true);
    setLineIndex(0);

    const lineTimers = [
      setTimeout(() => setLineIndex(1), 700),
      setTimeout(() => setLineIndex(2), 1300),
    ];

    const startedAt = Date.now();
    try {
      const res = await axios.post(`${BACKEND_URL}/api/prom/lead`, {
        school_name: school.trim(),
        prom_date: date.trim() || '',
        email: email.trim(),
        phone: phone.trim() || '',
        student_count: students ? parseInt(students, 10) : null,
        anon_id: getAnonId(),
        source: 'prom_landing_modal',
      });
      trackGrowth('prom_modal_submit', {
        metadata: { school_name: school.trim(), source: 'prom_landing_modal' },
      });
      const slug = res?.data?.slug;
      // Hold the composing copy for at least 1.8s so the rhythm reads
      // as deliberate composition, never as a service queue.
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, 1800 - elapsed);
      setTimeout(() => {
        lineTimers.forEach(clearTimeout);
        if (slug) navigate(`/prom/${slug}/ready`);
        else navigate('/prom/thank-you');
      }, remaining);
    } catch (err) {
      lineTimers.forEach(clearTimeout);
      const msg = err?.response?.data?.detail || 'That couldn\'t be composed · please try again.';
      setError(msg);
      setSubmitting(false);
      setLineIndex(0);
    }
  };

  return (
    <FolioBackdrop onClose={submitting ? undefined : onClose} testId="prom-modal-backdrop">
      <Folio testId="prom-setup-modal">
        {!submitting && (
          <FolioClose onClose={onClose} testId="prom-modal-close" />
        )}

        {submitting ? (
          <ComposingPhase
            line={COMPOSING_LINES[lineIndex]}
            school={school.trim()}
            testId="prom-modal-setup-progress"
            stepsTestId="prom-modal-setup-steps"
            stepIdPrefix="prom-setup-step"
            lineIndex={lineIndex}
          />
        ) : (
          <FormPhase
            school={school} setSchool={setSchool}
            date={date} setDate={setDate}
            email={email} setEmail={setEmail}
            phone={phone} setPhone={setPhone}
            students={students} setStudents={setStudents}
            error={error}
            onSubmit={handleSubmit}
            onSelfServe={onSelfServe}
          />
        )}
      </Folio>
    </FolioBackdrop>
  );
}


// ──────────────────────────────────────────────────────────────────
//  FORM PHASE — quiet editorial intake
// ──────────────────────────────────────────────────────────────────
function FormPhase({
  school, setSchool, date, setDate, email, setEmail,
  phone, setPhone, students, setStudents,
  error, onSubmit, onSelfServe,
}) {
  return (
    <>
      <FolioEyebrow>✦ Composing a prom night</FolioEyebrow>
      <FolioHeading>
        Tell us a little about the night
      </FolioHeading>
      <FolioLede>
        We'll compose the page, the QR plate, the missions and the live link from these few details. Nothing is shared with anyone outside EventDrop.
      </FolioLede>

      <form onSubmit={onSubmit} data-testid="prom-modal-form" style={{
        marginTop: 24, display: 'flex', flexDirection: 'column', gap: 18,
      }}>
        <FolioField label="School" required testid="prom-modal-school">
          <FolioInput
            type="text"
            value={school}
            onChange={(e) => setSchool(e.target.value)}
            placeholder="St. Mary's High"
            data-testid="prom-modal-school-input"
          />
        </FolioField>

        <FolioField label="The night of" testid="prom-modal-date">
          <FolioInput
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="prom-modal-date-input"
          />
        </FolioField>

        <FolioField label="Your email" required testid="prom-modal-email">
          <FolioInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teacher@school.edu"
            data-testid="prom-modal-email-input"
          />
        </FolioField>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 18,
        }}>
          <FolioField label="A phone" hint="if it helps" testid="prom-modal-phone">
            <FolioInput
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="07…"
              data-testid="prom-modal-phone-input"
            />
          </FolioField>
          <FolioField label="Students" hint="if known" testid="prom-modal-students">
            <FolioInput
              type="number"
              min="1"
              value={students}
              onChange={(e) => setStudents(e.target.value)}
              placeholder="120"
              data-testid="prom-modal-students-input"
            />
          </FolioField>
        </div>

        {error && (
          <div data-testid="prom-modal-error" style={{
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 12.5, color: '#A24A3F', letterSpacing: '0.005em',
          }}>{error}</div>
        )}

        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'stretch' }}>
          <FolioPrimaryButton type="submit" testId="prom-modal-submit">
            Have it composed ✦
          </FolioPrimaryButton>
          <div data-testid="prom-modal-social-proof" style={{
            textAlign: 'center',
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 12, color: 'rgba(40,32,18,0.5)', letterSpacing: '0.005em',
          }}>No commission required to begin · we'll write once.</div>
          <button
            type="button"
            onClick={() => { trackGrowth('prom_modal_self_serve_clicked'); onSelfServe && onSelfServe(); }}
            data-testid="prom-modal-self-serve"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 12.5, color: 'rgba(40,32,18,0.55)',
              textDecoration: 'underline',
              textDecorationColor: 'rgba(40,32,18,0.25)',
              textUnderlineOffset: 4,
              padding: '6px 0',
            }}
          >I'll compose it myself</button>
        </div>
      </form>
    </>
  );
}


// ──────────────────────────────────────────────────────────────────
//  COMPOSING PHASE — one quiet line, no stepper bubbles
// ──────────────────────────────────────────────────────────────────
export function ComposingPhase({ line, school, testId, stepsTestId, stepIdPrefix, lineIndex }) {
  return (
    <div data-testid={testId} style={{ padding: '8px 4px 4px' }}>
      <FolioEyebrow>✦ The page is being composed</FolioEyebrow>
      <FolioHeading>
        {school ? `Preparing the room for ${school}` : 'Preparing the room'}
      </FolioHeading>
      <FolioLede>
        A page, a QR plate and a live link are being composed together. It takes a quiet moment.
      </FolioLede>

      <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          aria-hidden
          style={{
            width: 8, height: 8, borderRadius: '50%',
            background: 'rgba(184,151,98,0.62)',
            animation: 'folioBreathe 1.8s ease-in-out infinite',
            flexShrink: 0,
          }}
        />
        <div
          aria-live="polite"
          style={{
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 14.5, color: 'rgba(40,32,18,0.78)',
            letterSpacing: '0.005em',
            transition: 'opacity 320ms ease',
          }}
        >{line}</div>
      </div>

      {/* Hidden semantic steps — preserves the previous testid surface
          for any external automation, without rendering bubble UI. */}
      <ul data-testid={stepsTestId} style={{ position: 'absolute', left: -9999, top: -9999 }}>
        {COMPOSING_LINES.map((l, idx) => (
          <li key={l} data-testid={`${stepIdPrefix}-${idx}`} data-active={idx === lineIndex ? '1' : '0'}>{l}</li>
        ))}
      </ul>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
//  FOLIO PRIMITIVES — parchment-edge editorial composition layer
// ──────────────────────────────────────────────────────────────────
//
// These primitives are exported so Year6SetupModal can reuse the
// same folio grammar. They are deliberately NOT in ModeTwoPrimitives
// — Mode 2 operational surfaces are graphite. The folios are
// parchment laid OVER the desk, which is a distinct layer.
export function FolioBackdrop({ children, onClose, testId }) {
  return (
    <div
      data-testid={testId}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 80,
        background: 'rgba(7,8,11,0.72)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(0px, 4vw, 32px)',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        maxWidth: 520, width: '100%',
        animation: 'folioRise 360ms cubic-bezier(0.16, 1, 0.3, 1) both',
      }}>{children}</div>
      <style>{`
        @keyframes folioRise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes folioBreathe {
          0%, 100% { opacity: 0.45; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}

export function Folio({ children, testId }) {
  // The folio sits as a parchment sheet inside a graphite frame.
  // The frame reads as the desk surface; the sheet reads as paper.
  return (
    <div
      data-testid={testId}
      style={{
        position: 'relative',
        background: '#F5F0E4',
        // Hairline gold edge + a calm graphite shadow that suggests
        // the sheet has been placed deliberately on the desk surface.
        border: '1px solid rgba(184,151,98,0.32)',
        borderRadius: 3,
        padding: 'clamp(28px, 4vw, 44px) clamp(24px, 4vw, 40px)',
        color: '#1B140A',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.4) inset, ' +
          '0 28px 60px rgba(0,0,0,0.55), ' +
          '0 0 0 8px rgba(184,151,98,0.04)',
        maxHeight: 'calc(100vh - 64px)',
        overflowY: 'auto',
      }}
    >{children}</div>
  );
}

export function FolioClose({ onClose, testId }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      data-testid={testId}
      style={{
        position: 'absolute', top: 14, right: 14,
        width: 28, height: 28,
        background: 'transparent',
        border: '1px solid rgba(40,32,18,0.18)',
        borderRadius: 3,
        cursor: 'pointer',
        color: 'rgba(40,32,18,0.55)',
        fontSize: 13, lineHeight: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >✕</button>
  );
}

export function FolioEyebrow({ children }) {
  return (
    <div style={{
      fontFamily: MODE_TWO.serif, fontStyle: 'italic',
      fontSize: 10.5, letterSpacing: '0.28em',
      textTransform: 'uppercase', color: 'rgba(40,32,18,0.55)',
    }}>{children}</div>
  );
}

export function FolioHeading({ children }) {
  return (
    <h2 style={{
      marginTop: 14, marginBottom: 0,
      fontFamily: MODE_TWO.serif, fontWeight: 500,
      fontSize: 'clamp(22px, 2.6vw, 28px)',
      letterSpacing: '-0.005em', lineHeight: 1.2,
      color: '#1B140A',
    }}>{children}</h2>
  );
}

export function FolioLede({ children }) {
  return (
    <p style={{
      marginTop: 12, marginBottom: 0,
      fontFamily: MODE_TWO.serif, fontStyle: 'italic',
      fontSize: 14, color: 'rgba(40,32,18,0.7)',
      lineHeight: 1.6, letterSpacing: '0.005em',
      maxWidth: 460,
    }}>{children}</p>
  );
}

export function FolioField({ label, hint, required, testid, children }) {
  return (
    <label data-testid={testid} style={{
      display: 'flex', flexDirection: 'column', gap: 7,
    }}>
      <span style={{
        fontFamily: MODE_TWO.sans,
        fontSize: 10.5, letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'rgba(40,32,18,0.6)',
        display: 'flex', alignItems: 'baseline', gap: 6,
      }}>
        {label}
        {required && <span style={{ color: 'rgba(184,151,98,0.85)' }}>·</span>}
        {hint && <span style={{
          fontStyle: 'italic', textTransform: 'none', letterSpacing: '0.01em',
          color: 'rgba(40,32,18,0.4)', fontSize: 11,
        }}>{hint}</span>}
      </span>
      {children}
    </label>
  );
}

export function FolioInput(props) {
  return (
    <input
      {...props}
      style={{
        background: 'rgba(255,251,242,0.55)',
        border: '1px solid rgba(40,32,18,0.18)',
        borderBottom: '1px solid rgba(40,32,18,0.32)',
        borderRadius: 2,
        padding: '10px 12px',
        fontFamily: MODE_TWO.serif,
        fontSize: 14, color: '#1B140A',
        letterSpacing: '0.005em',
        outline: 'none',
        transition: 'border-color 220ms ease, background 220ms ease',
        ...(props.style || {}),
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderBottomColor = 'rgba(184,151,98,0.7)';
        e.currentTarget.style.background = 'rgba(255,251,242,0.85)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderBottomColor = 'rgba(40,32,18,0.32)';
        e.currentTarget.style.background = 'rgba(255,251,242,0.55)';
      }}
    />
  );
}

export function FolioPrimaryButton({ children, type = 'button', onClick, testId, disabled }) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      data-pressed={pressed ? '1' : '0'}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      style={{
        appearance: 'none',
        background: pressed ? 'rgba(27,20,10,0.94)' : '#1B140A',
        color: '#F5F0E4',
        border: `1px solid ${pressed ? 'rgba(184,151,98,0.5)' : 'rgba(184,151,98,0.7)'}`,
        borderRadius: 3,
        padding: '14px 22px',
        fontFamily: MODE_TWO.serif, fontStyle: 'italic',
        fontSize: 14.5, letterSpacing: '0.01em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transform: pressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'transform 220ms ease, border-color 220ms ease, background 220ms ease',
      }}
    >{children}</button>
  );
}

export function FolioSecondaryButton({ children, onClick, testId, disabled }) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      data-pressed={pressed ? '1' : '0'}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      style={{
        appearance: 'none',
        background: 'transparent',
        color: '#1B140A',
        border: `1px solid ${pressed ? 'rgba(184,151,98,0.5)' : 'rgba(40,32,18,0.32)'}`,
        borderRadius: 3,
        padding: '12px 18px',
        fontFamily: MODE_TWO.serif, fontStyle: 'italic',
        fontSize: 13.5, letterSpacing: '0.01em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transform: pressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'transform 220ms ease, border-color 220ms ease',
      }}
    >{children}</button>
  );
}

export const FOLIO_HAIR = 'rgba(40,32,18,0.18)';
